import React, { useCallback, useEffect, useState } from 'react';
import { Checkbox, Modal } from 'antd';
import ProductsFilterForm from 'Components/DossierUnico/ProductsFilterForm';
import ProductsLookupResults from 'Components/DossierUnico/ProductsLookupResults';
import api from 'Common/api';
import { handleServerError } from 'Common/utils';
import PropTypes from 'prop-types';

const SearchProductsModal = ({
  visible,
  dossierId,
  onCancel,
  onAdd,
  isAdding,
  productRanges,
  disableProductRanges,
  productStates,
  disableProductStates,
}) => {
  const [searchResults, setSearchResults] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    if (!visible) {
      setSearchResults([]);
      setSelectedIds(new Set());
    }
  }, [visible]);

  const handleSearch = useCallback(async (values) => {
    try {
      const { data } = await api.products.lookup({
        ...values,
        scope: 'promo-locali',
        metadata: { promoLocaliId: String(dossierId) },
      });
      setSearchResults(data || []);
    } catch (e) {
      handleServerError(e);
    }
  }, [dossierId]);

  const handleSelection = useCallback((productId) => {
    setSelectedIds((prev) => {
      const next = new Set(Array.from(prev));

      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }

      return next;
    });
  }, []);

  const handleSelectAll = useCallback((checked) => {
    setSelectedIds(checked
      ? new Set(searchResults.map((product) => product.id))
      : new Set());
  }, [searchResults]);

  const handleOk = () => {
    onAdd(searchResults.filter((product) => selectedIds.has(product.id)));
  };

  const allSelected = searchResults.length > 0 && selectedIds.size === searchResults.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  return (
    <Modal
      title="Cerca e aggiungi referenze"
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Aggiungi"
      okButtonProps={{ disabled: selectedIds.size === 0, loading: isAdding }}
      cancelText="Annulla"
      centered
      width="90%"
      style={{ maxWidth: 1000 }}
      destroyOnClose
    >
      <ProductsFilterForm
        onSearch={handleSearch}
        hideOnlyAvailable
        defaultProductRanges={productRanges}
        disableProductRanges={disableProductRanges}
        defaultProductStates={productStates}
        disableProductStates={disableProductStates}
      />
      {searchResults.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={(e) => handleSelectAll(e.target.checked)}
            style={{ marginBottom: 8 }}
          >
            {`Seleziona tutti (${selectedIds.size}/${searchResults.length})`}
          </Checkbox>
          <ProductsLookupResults
            results={searchResults}
            selected={selectedIds}
            onSelection={handleSelection}
            multiple
          />
        </div>
      )}
    </Modal>
  );
};

SearchProductsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  dossierId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCancel: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  isAdding: PropTypes.bool,
  productRanges: PropTypes.arrayOf(PropTypes.string),
  disableProductRanges: PropTypes.bool,
  productStates: PropTypes.arrayOf(PropTypes.string),
  disableProductStates: PropTypes.bool,
};

export default SearchProductsModal;
